import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { teacherStudentJourney, teacherStudentStops, teacherStudentSummary } from "@/lib/api";
import { formatTeacherText } from "@/lib/markdown";

/**
 * Print-friendly one-page student journey report. Opens in a new tab.
 * Auto-triggers window.print() once rendered. @media print styles
 * remove the action bar.
 */
export default function TeacherStudentPrint() {
  const { learnerId } = useParams();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [journey, stops, summaryData] = await Promise.all([
          teacherStudentJourney(learnerId),
          teacherStudentStops(learnerId),
          teacherStudentSummary(learnerId),
        ]);
        setData({ journey, stops });
        // Only show summary if it has content
        if (summaryData.where_they_stand || summaryData.suggested_next_steps) {
          setSummary(summaryData);
        }
        // Small delay so the layout paints before print dialog
        setTimeout(() => { try { window.print(); } catch { /* ignore */ } }, 400);
      } catch (err) {
        console.error("Failed to load print data:", err);
      }
    })();
  }, [learnerId]);

  if (!data) {
    return <div className="p-10 text-center text-slate-400">Preparing report…</div>;
  }

  const { journey, stops } = data;
  const nodes = journey.sub_topic_nodes || [];
  const learnerName = journey.learner_name || "Student";
  const learnerGrade = journey.learner_grade || 5;
  const teacherName = journey.teacher_id || "teacher-default";
  const today = new Date().toLocaleDateString(undefined, {
    day: "numeric", month: "long", year: "numeric",
  });

  // Get primary subject (from first node or "Mathematics")
  const primarySubject = nodes.length > 0 ? nodes[0].subject : "Mathematics";
  // Extract class name from grade (e.g., Grade 5 → "Class 5A" - simplified)
  const className = `Class ${learnerGrade}`;

  // Helper to convert technical status to plain English
  const statusToPlainEnglish = (node) => {
    const status = node.status;
    if (status === "hard_locked") return "Locked";
    if (status === "pool_a_complete") return "Completed";
    if (status === "in_progress") return "In Progress";
    if (status === "not_started") return "Not Started";
    return status;
  };

  return (
    <div className="bg-white text-slate-900 min-h-screen print-page">
      {/* Print-specific CSS — embedded for portability */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .print-page { padding: 0 !important; }
          @page { size: A4 portrait; margin: 14mm 12mm; }
        }
        .print-page { padding: 32px 40px; max-width: 900px; margin: 0 auto; }
        .nodes-table thead { background: #f1f5f9; }
        .nodes-table th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;
                          text-align: left; padding: 8px 10px; border-bottom: 1px solid #cbd5e1;
                          color: #475569; font-weight: 700; }
        .nodes-table td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px;
                          vertical-align: top; }
        .summary-section { background: #f8fafc; padding: 16px; border-radius: 8px; margin-bottom: 16px;
                           border-left: 4px solid #0ea5e9; }
      `}</style>

      <div className="no-print flex justify-between items-center mb-6">
        <button
          onClick={() => window.close()}
          data-testid="print-close-btn"
          className="text-sm font-semibold text-slate-600 hover:text-slate-900"
        >
          ← Close
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            data-testid="print-trigger-btn"
            className="text-sm font-semibold bg-slate-900 text-white rounded-lg px-4 py-2 hover:bg-slate-800"
          >
            Print Report
          </button>
          <button
            onClick={() => window.print()}
            data-testid="download-pdf-btn"
            className="text-sm font-semibold bg-sky-600 text-white rounded-lg px-4 py-2 hover:bg-sky-700"
          >
            Download as PDF
          </button>
        </div>
      </div>

      <header className="border-b-2 border-slate-900 pb-4 mb-6" data-testid="print-header">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Student Journey Report</div>
        <div className="font-teacher text-3xl font-bold mt-1">
          {learnerName}'s Journey
        </div>
        <div className="text-sm text-slate-600 mt-2 flex flex-wrap gap-x-4 gap-y-1">
          <span><strong>Student:</strong> {learnerName}</span>
          <span><strong>Grade:</strong> {learnerGrade}</span>
          <span><strong>Subject:</strong> {primarySubject}</span>
          <span><strong>Teacher:</strong> {teacherName}</span>
          <span><strong>Class:</strong> {className}</span>
          <span><strong>Date:</strong> {today}</span>
        </div>
      </header>

      {/* Claude-generated summary sections */}
      {summary && (
        <div className="mb-6">
          {summary.where_they_stand && (
            <div className="summary-section" data-testid="print-where-stands">
              <h3 className="font-teacher text-base font-bold text-slate-900 mb-2">
                Where {learnerName.split(' ')[0]} stands right now
              </h3>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {formatTeacherText(summary.where_they_stand)}
              </p>
            </div>
          )}
          {summary.suggested_next_steps && (
            <div className="summary-section" data-testid="print-next-steps">
              <h3 className="font-teacher text-base font-bold text-slate-900 mb-2">
                Suggested next steps
              </h3>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {formatTeacherText(summary.suggested_next_steps)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Sub-topic nodes list */}
      <div className="mb-6">
        <h3 className="font-teacher text-lg font-semibold text-slate-900 mb-3">Sub-topic Progress</h3>
        {nodes.length === 0 ? (
          <div className="text-center text-slate-500 py-10" data-testid="print-empty">
            No journey data yet.
          </div>
        ) : (
          <table className="w-full nodes-table" data-testid="print-nodes">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Sub-topic</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Correct</th>
                <th>Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {nodes.map((n, i) => {
                const accuracy = n.attempts > 0 ? Math.round((n.correct / n.attempts) * 100) : 0;
                return (
                  <tr key={i} data-testid={`print-node-${i}`}>
                    <td className="font-semibold text-xs">{n.subject}</td>
                    <td>{n.sub_topic}</td>
                    <td className="text-xs">
                      <span className={
                        n.status === "pool_a_complete" ? "text-emerald-700 font-semibold" :
                        n.status === "hard_locked" ? "text-slate-500" :
                        "text-sky-700"
                      }>
                        {statusToPlainEnglish(n)}
                      </span>
                    </td>
                    <td className="text-center">{n.attempts}</td>
                    <td className="text-center">{n.correct}</td>
                    <td className="text-center">{accuracy}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Stop history summary */}
      {stops.length > 0 && (
        <div className="border-t pt-4">
          <h3 className="font-teacher text-base font-semibold text-slate-900 mb-2">Stop Events</h3>
          <p className="text-sm text-slate-600">
            This student has encountered <strong>{stops.length}</strong> STOP event{stops.length !== 1 ? 's' : ''} during their learning journey.
            These indicate moments where extra support may be needed.
          </p>
        </div>
      )}

      <footer className="border-t-2 border-slate-900 mt-6 pt-3 text-xs text-slate-600">
        <p>Generated by Adiora/Mitraa Learning Intelligence System · {today}</p>
        <p className="mt-1 italic">This report is intended for teacher and parent-teacher meetings (PTM).</p>
      </footer>
    </div>
  );
}
